import React, {useState, useEffect} from "react";
import ReactSVG from "react-svg";

import gql from "graphql-tag";
import {PermissionOnly, RootOnly} from "../../../components/AdminOnly";
import {Button} from "../../../components/Button";
import {Container, CardContainer} from "../../../components/Container";
import {CurrencyDisplay} from "../../../components/CurrencyDisplay";
import {H1, H2, Byline} from "../../../components/Typography";
import {Helmet} from "react-helmet";
import {Loading, LoadingError} from "../../../components/LoadingFork";
import {StatusPill} from "../../../components/StatusPill";
import {Table, THead, TBody} from "../../../components/Table";
import {Well} from "../../../components/Well";
import {css} from "glamor";
import {fmtDate} from "../../../lib/misc";
import {formatCurrency} from "../../../lib/rateCardUtils";
import {gray10, gray40} from "../../../theme";
import {useMutation, useQuery} from "@apollo/client";
import {withAppConfig} from "../../../components/AppConfigContext";
import {withInstallation} from "../../../components/InstallationContext";
import downloadIconSVG from "../../../icons/download.svg";
import {DRIVO_URL} from "../../../lib/drivoApollo";
import {DateInput} from "../../../components/DateInput";
import {DateTime} from "luxon";
import LoadingIndicator from "../../../components/LoadingIndicator";
import authManager, {downloadWithAuth, PERMISSIONS} from "../../../lib/authManager";
import {hideGlobalLoader, showGlobalLoader} from "../../../lib/loaderController";

export const createPayoutMutation = gql`
    mutation createPayout($createdAt: String) {
        createPayout(createdAt: $createdAt) {
            id
        }
    }
`;

export const financialsQuery = gql`
    query installations($installationId: ID!) {
        payouts(installationId: $installationId) {
            id
            createdAt
            finalisedAt
            amountAudRemit
        }
    }
`;

const statusFlavour = {
    PENDING: "warning",
    PROCESSED: "gray",
    REJECTED: "danger",
};

function DownloadLink({ children, href, filename }) {
    const handleClick = async (e) => {
        e.preventDefault();
        await downloadWithAuth({ url: href, filename });
    };

    return (
        <a href={href} onClick={handleClick} style={{ textDecoration: "none", color: "#007aff" }}>
            <ReactSVG
                src={downloadIconSVG}
                svgclassname={css({ display: "block" }).toString()}
                className={css({
                    display: "inline-block",
                    marginRight: 10,
                }).toString()}
            />
            {children}
        </a>
    );
}

export function getDefaultPayoutDate(payouts, timezone) {
    const now = DateTime.now().setZone(timezone);
    const yesterday = now.minus({ days: 1 });
    const lastFinalised = payouts[0] || null;

    /**
     * No payout history yet: default to yesterday (site timezone), the same as
     * when no payout has been made this month, so a site waiting on its first
     * payout sees its current balance rather than one frozen at the 1st.
     */
    if (!lastFinalised) {
        return yesterday;
    }

    const lastPayoutDate = DateTime.fromISO(lastFinalised.createdAt, { zone: "utc" })
        .setZone(timezone);

    const isSameMonth =
        lastPayoutDate.month === now.month &&
        lastPayoutDate.year === now.year;

    if (isSameMonth) {
        return now.plus({ months: 1 }).startOf("month");
    }

    /**
     * No payout yet this month: default to yesterday (site timezone) so the
     * balance shown matches the root admin default. Defaulting to the 1st of
     * the month froze "Available for payout" at that date (DRI-2708).
     * On the 1st, yesterday can be the last payout's own day, which the API
     * rejects, so fall forward to today; canRequestPayout keeps today disabled.
     */
    if (yesterday.startOf("day") <= lastPayoutDate.startOf("day")) {
        return now.startOf("day");
    }

    return yesterday;
}

export function canRequestPayout(availableUnpaid, payoutDate, timezone) {
    const payoutDay = DateTime.fromISO(payoutDate, {
        zone: timezone
    }).endOf('day');
    const now = DateTime.now().setZone(timezone);

    return availableUnpaid > 0 && payoutDay <= now;
}

function PayoutSummary({ available, future, currency, loading, isAdmin, payouts, installation, payoutDate, total }) {
    const amountStyle = css({ fontSize: 26, fontWeight: 800 });
    const labelStyle = css({ fontSize: 12, textTransform: "uppercase", color: "#666", marginBottom: 4 });
    const block = css({ display: "flex", flexDirection: "column", marginRight: 30 });

    const lastPayout = payouts[0] || null;
    const value = v =>
        loading ? <LoadingIndicator/> : formatCurrency(v,currency);

    if (!isAdmin) {
        return (
            <div className={css({ display: "flex", alignItems: "center" })}>
                <div className={block}>
                    <span className={labelStyle}>Available for payout</span>
                    <span className={amountStyle}>{value(available)}</span>
                </div>
                <div className={block}>
                    <span className={labelStyle}>Last payout date</span>
                    <span className={amountStyle}>{lastPayout ? fmtDate(lastPayout.createdAt, installation.timeZoneName, "d MMM yyyy") : '—'}</span>
                </div>
                <div className={block}>
                    <span className={labelStyle}>Next payout date</span>
                    <span className={amountStyle}>{DateTime.fromISO(payoutDate).toFormat("d MMM yyyy")}</span>
                </div>
            </div>
        );
    }

    return (
        <div className={css({ display: "flex", alignItems: "center" })}>
            <RootOnly>
                <div className={block}>
                    <span className={labelStyle}>Available for payout</span>
                    <span className={amountStyle}>{value(available)}</span>
                </div>
                <div className={block}>
                    <span className={labelStyle}>Pending (future bookings)</span>
                    <span className={amountStyle}>{value(future)}</span>
                </div>
                <div className={block}>
                    <span className={labelStyle}>Total unpaid balance</span>
                    <span className={amountStyle}>{value(total)}</span>
                </div>
            </RootOnly>
        </div>
    );
}

function PayoutDateSelector({ isAdmin, payoutDate, setPayoutDate, payouts, installation }) {
    if (!isAdmin) return null;

    return (
        <DateInput
            floatPicker
            placeholder="Enter date"
            autoComplete="off"
            displayFormat="ddd, D MMM YYYY"
            value={payoutDate}
            onChange={setPayoutDate}
            noMarginBottom
            isOutsideRange={day => {
                const minDate = payouts?.length
                    ? DateTime.fromISO(payouts[0].createdAt,{zone:"utc"})
                        .setZone(installation.timeZoneName)
                        .startOf("day")
                    : null;

                const maxDate = DateTime.now().endOf("day");

                return (
                    (minDate && day.isBefore(minDate.toJSDate())) ||
                    day.isAfter(maxDate.toJSDate())
                );

            }}
        />
    );
}

function PayoutRequestCard({
   availableUnpaid,
   futureUnpaid,
   totalUnpaid,
   installation,
   loading,
   payoutDate,
   setPayoutDate,
   payouts,
   allowRequest,
   onSubmit
}) {
    return (
        <div className={css({
            display:"flex",
            flexDirection:"column",
            backgroundColor:gray10,
            borderRadius:6,
            padding:"16px 20px",
            gap:10
        })}>
            <div className={css({
                display:"flex",
                alignItems:"center",
                justifyContent:"space-between"
            })}>
                <PayoutSummary
                    available={availableUnpaid}
                    future={futureUnpaid}
                    total={totalUnpaid}
                    currency={installation.currency}
                    loading={loading}
                    payouts={payouts}
                    installation={installation}
                    payoutDate={payoutDate}
                    isAdmin={authManager.isRootAdmin()}
                />
              <PermissionOnly permissions={PERMISSIONS.PAYOUTS_MANAGE}>
                <div className={css({display:"flex",alignItems:"center",gap:20})}>
                    <PayoutDateSelector
                        isAdmin={authManager.isRootAdmin()}
                        payoutDate={payoutDate}
                        setPayoutDate={setPayoutDate}
                        payouts={payouts}
                        installation={installation}
                    />
                    <Button
                        type="submit"
                        style={{backgroundColor: "#3127B8", margin: 0}}
                        onClick={onSubmit}
                        disabled={!allowRequest || loading}
                    >
                        Request payout
                    </Button>
                </div>
              </PermissionOnly>
            </div>
        </div>
    );
}

function _PayoutSummaryRow({
   id,createdAt,finalisedAt,
   amountAudRemit,currency,timezone,
   brandingKey,isRootAdmin
}) {
    const status = finalisedAt ? "PROCESSED" : "PENDING";
    const acceptUrl = finalisedAt
        ? null
        : `/payouts/${id}/mark-as-processed`;

    return (
        <tr>

            <td>
                {isRootAdmin
                    ? <a href={`${window.location.href}/${id}`}>
                        {fmtDate(createdAt,timezone,"d MMM yyyy")}
                    </a>
                    : fmtDate(createdAt,timezone,"d MMM yyyy")}

                {acceptUrl && (
                    <RootOnly>
                        <a href={acceptUrl} className={css({ fontSize: 12, color: gray40 })}>
                            {' '}
                            (mark as processed)
                        </a>
                    </RootOnly>
                )}
            </td>
            <td>{fmtDate(finalisedAt,timezone,"d MMM yyyy")}</td>
            <PermissionOnly permissions={PERMISSIONS.REPORTS_EXPORT}>
              <td>
                  <DownloadLink
                      href={`${DRIVO_URL}/payouts/${id}/download`}
                      filename={`payout_${brandingKey}_${fmtDate(createdAt,timezone,"yyyyMMdd")}.xlsx`}
                  >
                      Spreadsheet (.xlsx)
                  </DownloadLink>
              </td>
            </PermissionOnly>
            <td>
                <strong>
                    <CurrencyDisplay amountString={amountAudRemit} currency={currency}/>
                </strong>
            </td>
            <td>
                <StatusPill flavour={statusFlavour[status]}>{status}</StatusPill>
            </td>
        </tr>
    );
}

const PayoutSummaryRow = withAppConfig(_PayoutSummaryRow);

function Payouts({ installation }) {
    const isRootAdmin = authManager.isRootAdmin();
    const [payoutDate, setPayoutDate] = useState(null);
    const { data = {}, loading,error: historyError, refetch} = useQuery(
        financialsQuery, {
            fetchPolicy: "no-cache",
            variables: { installationId:installation.id },
        });
    const { payouts = [] } = data;

    useEffect(() => {
        const defaultDate = isRootAdmin
            ? DateTime.now().minus({ days: 1 })
            : getDefaultPayoutDate(payouts, installation.timeZoneName);

        setPayoutDate(defaultDate.toFormat("yyyy-MM-dd"));

    },[payouts]);

    const {data: drivoData, error: drivoError, refetch: refetchFinancials, loading: drivoLoading} = useQuery(
        gql`
            query Drivo($id: ID!, $endDate: String) {
                facility: facilityByInstallation(installation: $id, endDate: $endDate) {
                id
                financials
            }
        }`,
        {
            variables: { id: installation.id, endDate: payoutDate },
            fetchPolicy: "no-cache",
            skip: !payoutDate,
        }
    );

    const financials = drivoData?.facility?.financials || {};

    const totalUnpaid = Number(financials.unpaid || 0);
    const availableUnpaid = Number(financials.available || 0);
    const futureUnpaid = Number(financials.future || 0);

    // disable submit request, if payout is in the future
    const allowRequest = canRequestPayout(availableUnpaid, payoutDate, installation.timeZoneName);

    const [createPayoutRequest, { error:requestError }] = useMutation(createPayoutMutation);
    const error = historyError || requestError;

    async function onSubmit(){
        try {
            showGlobalLoader();
            await createPayoutRequest({
                variables: { createdAt: payoutDate },
            });

            await Promise.all([refetch(), refetchFinancials()]);
        } catch (e) {
            console.error(e.message);
        } finally {
            hideGlobalLoader();
        }
    }

    return (
        <div>
            <CardContainer>
                <Helmet>
                    <title>Payouts</title>
                </Helmet>
                <Container size="large" spaceAbove="small">
                    <H1>Payouts</H1>
                </Container>
                <Container size="large" spaceBelow="medium">
                    {loading && <Loading/>}
                    {error && <LoadingError error={error}/>}

                    {/**
                      * Without payout history the default date can't be trusted
                      * (it would look like a site that was never paid out), so
                      * don't offer a request until the history loads.
                      */}
                    {!loading && !historyError && payoutDate && (
                        <PayoutRequestCard
                            availableUnpaid={availableUnpaid}
                            futureUnpaid={futureUnpaid}
                            totalUnpaid={totalUnpaid}
                            installation={installation}
                            loading={drivoLoading}
                            payoutDate={payoutDate}
                            setPayoutDate={setPayoutDate}
                            payouts={payouts}
                            allowRequest={allowRequest}
                            onSubmit={onSubmit}
                        />
                    )}
                </Container>
                <Container spaceBelow="large">
                    {loading ? null :
                        payouts.length === 0 ? (
                            <Well>
                                <H2>No payouts available</H2>
                                <Byline>Use the form above to create your first payout.</Byline>
                            </Well>
                        ) : (
                            <>
                                <H2>Recent payouts</H2>
                                <Table>
                                    <THead>
                                        <tr>
                                            <th>Requested</th>
                                            <th>Processed</th>
                                            <PermissionOnly permissions={PERMISSIONS.REPORTS_EXPORT}>
                                              <th>Download</th>
                                            </PermissionOnly>
                                            <th>Amount</th>
                                            <th>Status</th>
                                        </tr>
                                    </THead>
                                    <TBody>
                                        {payouts.map(payout => (
                                            <PayoutSummaryRow
                                                key={payout.id}
                                                {...payout}
                                                currency={installation.currency}
                                                timezone={installation.timeZoneName}
                                                brandingKey={installation.brandingKey}
                                                isRootAdmin={isRootAdmin}
                                            />
                                        ))}
                                    </TBody>
                                </Table>
                            </>
                        )}
                </Container>
            </CardContainer>
        </div>
    );
}

export default withInstallation(Payouts);
